/*
 * RAUMEN global.js — shared helpers + conventions (read this before building a section).
 *
 * ---------------------------------------------------------------------------
 * CONVENTIONS
 * ---------------------------------------------------------------------------
 * Global namespace:  window.RM  (config in window.RM_CONFIG, written by layout/theme.liquid)
 *   RM.config                      routes + strings + cartType + freeShippingThreshold
 *   RM.debounce(fn, ms)            standard debounce
 *   RM.trapFocus(container)        returns release() — for non-<dialog> overlays. <dialog>.showModal() already traps focus
 *   RM.fetchSection(id, opts)      GET the Section Rendering API -> HTML string. opts: { url, params, signal }
 *   RM.fetchJSON(url, init)        fetch + JSON with error handling
 *   RM.announce(message)           polite screen-reader announcement ([data-live-region])
 *   RM.openDrawer() / RM.closeDrawer()   open / close the cart drawer (falls back to the cart page when cartType = page)
 *   RM.addToCart(items, opts)      Ajax add (items = [{id, quantity, properties}]); opens the drawer. Defined in product-form.js
 *   RM.openQuickView(url, trigger) open the quick-view modal. Defined in quick-view.js
 *
 * Custom events (all dispatched on `document`, bubbles):
 *   cart:updated   detail { cart, sections, source }   fired after ANY cart mutation (add / change / update). Header count and drawer listen.
 *   cart:open      request to open the drawer          (document.dispatchEvent(new CustomEvent('cart:open')))
 *   cart:close     request to close the drawer
 *   cart:error     detail { message }                  surfaced inside the drawer
 *   quickview:open detail { url }                      request to open quick view
 *   quickview:opened / quickview:closed
 *   search:open / search:close                         header search state
 *
 * HTML hooks:
 *   [data-cart-open]            click opens the drawer (anchor to /cart keeps working without JS)
 *   [data-cart-close]           click closes the drawer
 *   [data-cart-count]           text is replaced with cart.item_count; `hidden` when 0
 *   [data-cart-status]          sr-only live text "Cart, N items"
 *   [data-quick-view="/products/handle"]   click opens the quick-view modal for that product
 *   <product-form><form action="/cart/add">…</form></product-form>   Ajax add, then opens the drawer
 *
 * Snippets:
 *   {% render 'product-card', product: p, rank: n, show_quick_add: true, quick_add_mode: 'modal'|'direct', show_rating: true, lazy: true, sizes: '…', class: '' %}
 *   {% render 'price', product: p, variant: v, size: 'sm'|'md'|'lg' %}
 *   {% render 'icon', name: 'cart', size: 20, stroke: 1.5, class: '' %}
 *       names: cart search user close plus minus chevron chevron-right chevron-left arrow-right arrow-left menu whatsapp
 *              check star truck shield cash tag gift file mail phone pin lock heart play external facebook instagram
 *              tiktok shopee lazada
 *   {% render 'section-tag', text: 'Best seller', size: 'sm'|'md', class: '' }
 *   {% render 'trust-bar', variant: 'light'|'brand', class: '' %}
 *   {% render 'button', label: '…', url: '…', style: 'primary'|'outline'|'silver', size: 'md'|'lg', block: true, icon: 'arrow-right', type: 'button'|'submit', class: '', attrs: '' %}
 *   {% render 'breadcrumbs', current: '' %}
 *   {% render 'rating-stars', rating: 4.8, count: 126, show_value: true %}
 *   {% render 'payment-chips', variant: 'light'|'dark' %}
 *   {% render 'faq-accordion', blocks: section.blocks, open_first: true, name: section.id %}   (blocks expose settings.question / settings.answer)
 *   {% render 'image', image: obj, fallback: 'hero.jpg', fallback_width: 1920, fallback_height: 1080, alt: '', sizes: '100vw', widths: '320, 640', loading: 'lazy'|'eager', class: '', style: '' %}
 *
 * CSS classes (assets/base.css):
 *   Layout    .page-width (max 1440, gutters 16/32/48)  .rm-section  .rm-section--tight  .rm-grid  .rm-stack  .rm-cluster
 *   Buttons   .rm-btn  .rm-btn--outline  .rm-btn--silver  .rm-btn--lg  .rm-btn--block
 *   Badges    .rm-badge  .rm-badge--solid  .rm-badge--ink  .rm-badge--silver
 *   Tag       .rm-tag .rm-tag--sm (use the section-tag snippet)
 *   Headings  .rm-display (Anton)  + --hero --xl --lg --md --sm    .rm-heading (Montserrat italic 500)   .rm-eyebrow   .rm-lead   .rm-muted
 *   Links     .rm-link-arrow
 *   Surfaces  .rm-dark (dark token scope)  .rm-light  .rm-surface-dark  .rm-surface-brand
 *   Forms     .rm-field .rm-field__label .rm-input .rm-error
 *   Motion    .rm-reveal (scroll reveal)  .rm-marquee/.rm-marquee__track  .rm-line(+--d2/--d3) .rm-fade-in  .silver-text
 *   Misc      .visually-hidden  .rte
 *   Tokens    --navy-500/600/700/800/900 --black --white --silver-100/300 --ice --surface --text --text-muted --line --line-strong
 *             --space-1..16 --page-gutter --section-pad --header-height --ease-out
 *
 * Breakpoints: 750px (tablet/mobile switch), 1100px (desktop header + mega menu). Design widths: 1440 / 390.
 * Product metafields read by the shared code (namespace raumen): step_number, step_label, role, card_meta;
 * standard review metafields: reviews.rating, reviews.rating_count.
 *
 * quick-view contract (needed by whichever agent builds sections/quick-view.liquid):
 *   GET {product.url}?section_id=quick-view  -> the section must render a root element
 *   `<div data-quick-view-root>` containing the gallery, title, price, a <product-form> with the variant form,
 *   and a link to the full product page. Inside a modal, product-form closes the modal after a successful add.
 * ---------------------------------------------------------------------------
 */
(() => {
  const RM = (window.RM = window.RM || {});
  RM.config = window.RM_CONFIG || { routes: {}, strings: {} };

  /** @param {Function} fn @param {number} [wait] */
  RM.debounce = (fn, wait = 250) => {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn(...args), wait);
    };
  };

  const FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  /**
   * Trap Tab focus inside a container (for overlays that are not native <dialog>).
   * @param {HTMLElement} container
   * @returns {() => void} release function (also returns focus to the previously focused element)
   */
  RM.trapFocus = (container) => {
    const previous = document.activeElement;
    const onKeydown = (event) => {
      if (event.key !== 'Tab') return;
      const items = [...container.querySelectorAll(FOCUSABLE)].filter((el) => !el.hidden && el.offsetParent !== null);
      if (items.length === 0) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    container.addEventListener('keydown', onKeydown);
    return () => {
      container.removeEventListener('keydown', onKeydown);
      if (previous instanceof HTMLElement) previous.focus();
    };
  };

  /**
   * @param {string} url
   * @param {RequestInit} [init]
   */
  RM.fetchJSON = async (url, init = {}) => {
    const response = await fetch(url, {
      ...init,
      headers: { Accept: 'application/json', ...(init.headers || {}) },
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      const error = new Error(data.description || data.message || `HTTP ${response.status}`);
      error.status = response.status;
      error.data = data;
      throw error;
    }
    return data;
  };

  /**
   * Fetch rendered section HTML through the Section Rendering API.
   * @param {string} id - section id (static section name or section-group key)
   * @param {{url?: string, params?: Record<string, string>, signal?: AbortSignal}} [options]
   * @returns {Promise<string>}
   */
  RM.fetchSection = async (id, { url = window.location.pathname, params = {}, signal } = {}) => {
    const target = new URL(url, window.location.origin);
    target.searchParams.set('section_id', id);
    for (const [key, value] of Object.entries(params)) target.searchParams.set(key, value);
    const response = await fetch(target.toString(), { signal });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    return response.text();
  };

  /** @param {string} message */
  RM.announce = (message) => {
    const region = document.querySelector('[data-live-region]');
    if (!region) return;
    region.textContent = '';
    requestAnimationFrame(() => {
      region.textContent = message;
    });
  };

  RM.openDrawer = () => {
    if (RM.config.cartType === 'page') {
      window.location.href = RM.config.routes.cart;
      return;
    }
    document.dispatchEvent(new CustomEvent('cart:open'));
  };
  RM.closeDrawer = () => document.dispatchEvent(new CustomEvent('cart:close'));

  /* Footer columns: accordions on mobile, always-open columns from 750px. */
  const footerMedia = window.matchMedia('(min-width: 750px)');
  const syncFooterGroups = () => {
    for (const group of document.querySelectorAll('details[data-footer-group]')) {
      group.open = footerMedia.matches;
    }
  };
  syncFooterGroups();
  footerMedia.addEventListener('change', syncFooterGroups);

  /* Keep cart count + sr status in sync everywhere. */
  document.addEventListener('cart:updated', (event) => {
    const cart = event.detail && event.detail.cart;
    if (!cart || typeof cart.item_count !== 'number') return;
    for (const el of document.querySelectorAll('[data-cart-count]')) {
      el.textContent = String(cart.item_count);
      el.hidden = cart.item_count === 0;
    }
    const strings = RM.config.strings || {};
    const status =
      cart.item_count === 1
        ? strings.cartStatusOne
        : (strings.cartStatusOther || '').replace('__N__', String(cart.item_count));
    for (const el of document.querySelectorAll('[data-cart-status]')) el.textContent = status;
  });

  /* Scroll reveal fallback for browsers without animation-timeline (IntersectionObserver). */
  if (!CSS.supports('animation-timeline: view()')) {
    document.documentElement.classList.add('no-view-timeline');
  }
})();
